import {
  ReactElement,
  ChangeEvent,
  MouseEvent,
  useCallback,
  useMemo,
} from 'react';
import {
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  Divider,
  Typography,
  ToggleButton,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { lowerFirst } from 'lodash';

import { ScheduleRouteWeeklyDayIndex } from '~services/scheduleRoute/types';
import { hasError, getHelperText } from '~utils/formHelpers';
import { formatterDate } from '~utils/formatter';
import {
  isRouteScheduleRepetionTypeWeekly,
  getRouteScheduleRepitionDescriptionUnit,
} from '~utils/scheduleRoute/repetionType';
// eslint-disable-next-line max-len
import { buildScheduleRouteRepetionDescription } from '~utils/scheduleRoute/repetionDescription/builder';
import {
  SCHEDULE_ROUTE_REPETION_TYPE_OPTIONS,
  SCHEDULE_ROUTE_WEEKLY_DAYS_OPTIONS,
} from '~constants/scheduleRoute';

import {
  SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME,
  getScheduleRouteBaseFormInititalValues,
} from '../../../utils/scheduleRouteForm';
import { AssistantGenerateRoutesDialogInfoAlert } from '../../../styles';
import { AssistantGenerateRoutesDialogPreAsignedDriverAction } from '../../../Components';

import { AssistantGenerateRoutesDialogScheduleRouteCardProps } from '../types';

import { AssistantGenerateRoutesDialogScheduleRouteCardFormToggleButtonGroup } from './styles';

const AssistantGenerateRoutesDialogScheduleRouteCardForm = ({
  startDate: scheduleStartDateValue,
  infoText,
  withPreAssignedDriver = false,
  currentPreAssignedDriver,
  onAssignedDriver,
}: Pick<
  AssistantGenerateRoutesDialogScheduleRouteCardProps,
  | 'startDate'
  | 'infoText'
  | 'withPreAssignedDriver'
  | 'currentPreAssignedDriver'
  | 'onAssignedDriver'
>): ReactElement => {
  const {
    touched,
    errors,
    values,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } =
    useFormikContext<
      ReturnType<typeof getScheduleRouteBaseFormInititalValues>
    >();

  const isEnabled = useMemo(
    () => values[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED],
    [values],
  );

  const scheduleRouteRepetionTypeValue = useMemo(
    () =>
      values[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE],
    [values],
  );

  const handleChangeScheduleRouteRepetionType = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setFieldValue(
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE,
        value,
        false,
      );
      setFieldValue(
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS,
        [],
        false,
      );

      await validateForm();
    },
    [setFieldValue, validateForm],
  );

  const scheduleRouteRepetionIntervalCountValue = useMemo(
    () =>
      values[
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME
          .SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT
      ],
    [values],
  );

  const minDateToFieldScheduleEndDate = useMemo(() => {
    if (!scheduleStartDateValue) return undefined;

    return moment(scheduleStartDateValue, moment.HTML5_FMT.DATE)
      .startOf('day')
      .add(1, 'day')
      .toDate();
  }, [scheduleStartDateValue]);

  const scheduleEndDateValue = useMemo(
    () => values[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE],
    [values],
  );

  const handleChangeScheduleEndDate = useCallback(
    (value: Date | null): void => {
      setFieldValue(
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE,
        value,
      );
    },
    [setFieldValue],
  );

  const handleBlurScheduleEndDate = useCallback((): void => {
    setFieldTouched(
      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE,
      true,
    );
  }, [setFieldTouched]);

  const scheduleReptionIntervalDaysValue = useMemo(
    () =>
      values[
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME
          .SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS
      ],
    [values],
  );

  const handleChangescheduleReptionIntervalDays = useCallback(
    (
      _event: MouseEvent<HTMLElement>,
      value: ScheduleRouteWeeklyDayIndex[],
    ): void => {
      setFieldValue(
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS,
        value,
      );
    },
    [setFieldValue],
  );

  const handleBlurscheduleReptionIntervalDays = useCallback((): void => {
    setFieldTouched(
      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS,
      true,
    );
  }, [setFieldTouched]);

  const resultDescription = useMemo(() => {
    let repetionDescription: undefined | string = undefined;

    if (scheduleRouteRepetionIntervalCountValue > 0) {
      repetionDescription = buildScheduleRouteRepetionDescription({
        repetitionTypeId: scheduleRouteRepetionTypeValue,
        repeatInterval: scheduleRouteRepetionIntervalCountValue,
        repetitionIntervalArray: scheduleReptionIntervalDaysValue,
        dayFormat: 'long',
      });

      if (
        isRouteScheduleRepetionTypeWeekly(scheduleRouteRepetionTypeValue) &&
        scheduleReptionIntervalDaysValue.length === 0
      ) {
        repetionDescription = undefined;
      }
    }

    let datesDescription: undefined | string = undefined;

    if (scheduleStartDateValue && scheduleEndDateValue) {
      const startDateFormatter = formatterDate(scheduleStartDateValue, {
        format: 'DD/MM/YYYY',
      });

      const endDateFormatter = formatterDate(scheduleEndDateValue, {
        format: 'DD/MM/YYYY',
      });

      datesDescription = `${startDateFormatter} hasta ${endDateFormatter}`;
    }

    if (repetionDescription && datesDescription) {
      return `Se produce ${lowerFirst(
        repetionDescription,
      )} empezando el ${datesDescription}`;
    }

    return undefined;
  }, [
    scheduleEndDateValue,
    scheduleReptionIntervalDaysValue,
    scheduleRouteRepetionIntervalCountValue,
    scheduleRouteRepetionTypeValue,
    scheduleStartDateValue,
  ]);

  return (
    <Stack
      spacing={2}
      marginTop={0.75}
      paddingTop={2}
      paddingBottom={1}
      borderTop="1px solid #dee2e6"
      divider={<Divider />}
    >
      <Stack spacing={2} divider={<Divider />}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              {...getFieldProps(
                SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE,
              )}
              label="Periodicidad"
              onChange={handleChangeScheduleRouteRepetionType}
              error={hasError(
                touched,
                errors,
                SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE,
              )}
              helperText={getHelperText(
                touched,
                errors,
                SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE,
              )}
              select
              fullWidth
              autoComplete="off"
              disabled={!isEnabled}
            >
              {SCHEDULE_ROUTE_REPETION_TYPE_OPTIONS.map((option) => (
                <MenuItem
                  key={`schedule-route-repetion-type-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              {...getFieldProps(
                SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT,
              )}
              label="Cada"
              type="number"
              error={hasError(
                touched,
                errors,
                SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT,
              )}
              helperText={getHelperText(
                touched,
                errors,
                SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT,
              )}
              fullWidth
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getRouteScheduleRepitionDescriptionUnit(
                      scheduleRouteRepetionTypeValue,
                      scheduleRouteRepetionIntervalCountValue,
                    )}
                  </InputAdornment>
                ),
              }}
              disabled={!isEnabled}
            />

            <DatePicker
              label="Fecha de finalización"
              value={scheduleEndDateValue}
              onChange={handleChangeScheduleEndDate}
              minDate={minDateToFieldScheduleEndDate}
              disablePast
              disableHighlightToday
              disabled={!isEnabled}
              renderInput={(props) => (
                <TextField
                  {...props}
                  onBlur={handleBlurScheduleEndDate}
                  error={hasError(
                    touched,
                    errors,
                    SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE,
                  )}
                  helperText={getHelperText(
                    touched,
                    errors,
                    SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE,
                  )}
                  fullWidth
                />
              )}
            />
          </Stack>

          <Stack spacing={0.5}>
            {isRouteScheduleRepetionTypeWeekly(
              scheduleRouteRepetionTypeValue,
            ) && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Días de repetición (selecciona qué día o días se va a
                  programar la ruta).
                </Typography>

                <Stack>
                  <AssistantGenerateRoutesDialogScheduleRouteCardFormToggleButtonGroup
                    value={scheduleReptionIntervalDaysValue}
                    onChange={handleChangescheduleReptionIntervalDays}
                    onBlur={handleBlurscheduleReptionIntervalDays}
                    disabled={!isEnabled}
                  >
                    {SCHEDULE_ROUTE_WEEKLY_DAYS_OPTIONS.map((option) => (
                      <ToggleButton
                        key={`schedule-route-weekly-days-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </ToggleButton>
                    ))}
                  </AssistantGenerateRoutesDialogScheduleRouteCardFormToggleButtonGroup>

                  <FormHelperText
                    error={hasError(
                      touched,
                      errors,
                      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS,
                    )}
                  >
                    {getHelperText(
                      touched,
                      errors,
                      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS,
                    )}
                  </FormHelperText>
                </Stack>
              </Stack>
            )}

            <Typography variant="caption" color="text.secondary">
              Solo se crearán los primeros 15 eventos.
            </Typography>
          </Stack>

          {infoText && (
            <AssistantGenerateRoutesDialogInfoAlert text={infoText} />
          )}
        </Stack>

        {resultDescription && (
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Resultado de la programación
            </Typography>

            <Typography variant="body2" fontWeight="bold">
              {resultDescription}
            </Typography>
          </Stack>
        )}
      </Stack>

      {withPreAssignedDriver &&
        currentPreAssignedDriver !== undefined &&
        onAssignedDriver && (
          <AssistantGenerateRoutesDialogPreAsignedDriverAction
            currentDriver={currentPreAssignedDriver}
            onAssigned={onAssignedDriver}
            disabled={!isEnabled}
          />
        )}
    </Stack>
  );
};

export default AssistantGenerateRoutesDialogScheduleRouteCardForm;
